// 总推荐状态 tag — a member's overall recommendation status, computed by the
// backend from the matches table (backend/utils/recStatus.js):
//   together    已交往       any match 'in_relationship'
//   dating      已约见       else any match 'succeeded' / 'successful'
//   contact     接触中       else any 'in_contact' / 'in_progress'
//   recommended 已推荐 · N   else any non-cancelled match (N = count)
//   none        未推荐       no non-cancelled match
//
// Usage:
//   <RecStatusTag profile={p} />      uses p.rec_status / p.rec_count / p.rec_matches
//   <RecStatusTag profileId={id} />   fetches GET /profiles/:id/rec-status
// Renders nothing when the data is missing (old backend / request failed).

import React, { useEffect, useState } from 'react';
import { recStatusAPI } from '../../services/api';

export const REC_STATUS_OPTIONS = [
  { key: 'together', zh: '已交往', en: 'Dating' },
  { key: 'dating', zh: '已约见', en: 'Met' },
  { key: 'contact', zh: '接触中', en: 'In contact' },
  { key: 'recommended', zh: '已推荐', en: 'Recommended' },
  { key: 'none', zh: '未推荐', en: 'Not recommended' },
];

const STYLES = {
  together: { background: '#3F7D5A', color: '#FFFDF9', border: '1px solid #3F7D5A' },
  dating: { background: '#C2671E', color: '#FFFDF9', border: '1px solid #C2671E' },
  contact: { background: '#E6EFE9', color: '#2F5E44', border: '1px solid #E6EFE9' },
  recommended: { background: '#EFEAE2', color: '#3A3835', border: '1px solid #EFEAE2' },
  none: { background: 'transparent', color: '#6E685F', border: '1px solid rgba(17,17,17,.25)' },
};

const MATCH_STATUS_ZH = {
  pending: '待确认', approved: '已确认', rejected: '已拒绝',
  succeeded: '已约见', successful: '已约见', in_contact: '接触中', in_progress: '接触中', in_relationship: '已交往',
  failed: '未成功', cancelled: '已取消', completed: '已完成',
};

export function recStatusLabel(status, count, isZh = true) {
  const opt = REC_STATUS_OPTIONS.find((o) => o.key === status);
  if (!opt) return '';
  const base = isZh ? opt.zh : opt.en;
  return status === 'recommended' && count ? `${base} · ${count}` : base;
}

function hoverTitle(status, count, matches, isZh) {
  const head = recStatusLabel(status, count, isZh);
  const list = Array.isArray(matches) ? matches : [];
  if (!list.length) return head;
  const lines = list.map((m) => `${m.partner || ''} · ${MATCH_STATUS_ZH[m.status] || m.status || '—'}`);
  const more = count > list.length ? [`…${isZh ? '共' : 'total'} ${count}`] : [];
  return [head, ...lines, ...more].join('\n');
}

export default function RecStatusTag({ profile, profileId, isZh = true, size = 'sm', style }) {
  const has = profile && typeof profile.rec_status === 'string';
  const [fetched, setFetched] = useState(null);
  const id = profileId || null;
  useEffect(() => {
    if (has || !id) return undefined;
    let alive = true;
    setFetched(null);
    recStatusAPI.get(id)
      .then((r) => { if (alive && r?.data && typeof r.data.rec_status === 'string') setFetched(r.data); })
      .catch(() => { /* old backend → render nothing */ });
    return () => { alive = false; };
  }, [has, id]);

  const src = has ? profile : fetched;
  if (!src || !STYLES[src.rec_status]) return null;
  const count = Number(src.rec_count || 0);
  const big = size === 'md';
  return (
    <span
      title={hoverTitle(src.rec_status, count, src.rec_matches, isZh)}
      style={{
        display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', lineHeight: 1.4,
        fontSize: big ? 13 : 12, padding: big ? '3px 10px' : '2px 8px', borderRadius: 2,
        fontWeight: 400, letterSpacing: 0, verticalAlign: 'middle', cursor: 'default',
        ...STYLES[src.rec_status],
        ...(style || {}),
      }}
    >
      {recStatusLabel(src.rec_status, count, isZh)}
    </span>
  );
}
